import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  InputAdornment,
  Skeleton,
  Paper,
  MenuItem,
  CardContent,
  Avatar,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Edit,
  Delete,
  Add,
  Refresh,
  Quiz,
  Star,
  AutoAwesome,
  Upload,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface CoffeeQuestion {
  id: string;
  answer: string;
  wordCount: number;
  hints: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  timesAsked: number;
  lastUsedDate: Date | null;
}

interface DailyQuestion {
  id: string;
  answer: string;
  wordCount: number;
  difficulty: string;
  availableAt: string;
  expiresAt: string;
}

interface LeaderboardEntry {
  userId: string;
  username: string;
  photoURL: string;
  totalScore: number;
  gamesPlayed: number;
  gamesWon: number;
  averageGuesses: number;
}

interface MonthlyWinner {
  userId: string;
  username: string;
  photoURL: string;
  rank: number;
  monthYear: string;
  rewardAmount: number;
  badge: string;
}

const GameManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<CoffeeQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<CoffeeQuestion | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<CoffeeQuestion | null>(null);
  const [dailyQuestion, setDailyQuestion] = useState<DailyQuestion | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [monthlyWinners, setMonthlyWinners] = useState<MonthlyWinner[]>([]);
  const [aiLoading, setAiLoading] = useState(false);

  const [jobStatus, setJobStatus] = useState<any>(null);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    fetchQuestions();
    fetchDailyQuestion();
    fetchLeaderboard();
    fetchMonthlyWinners();
    fetchJobStatus();
  }, []);

  // Form states
  const [formData, setFormData] = useState({
    answer: '',
    hint1: '',
    hint2: '',
    hint3: '',
    hint4: '',
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
  });

  useEffect(() => {
    fetchQuestions();
    fetchDailyQuestion();
    fetchLeaderboard();
    fetchMonthlyWinners();
  }, []);

  const fetchDailyQuestion = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/game/daily`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setDailyQuestion(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching daily question:', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/game/leaderboard?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setLeaderboard(response.data.data.leaderboard || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const fetchMonthlyWinners = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/game/monthly-winners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setMonthlyWinners(response.data.data.winners || []);
      }
    } catch (error) {
      console.error('Error fetching monthly winners:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.get(`${API_BASE_URL}/api/game/questions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        const questionsList = response.data.data.questions.map((q: any) => ({
          ...q,
          lastUsedDate: q.lastUsedDate ? new Date(q.lastUsedDate) : null,
        }));
        setQuestions(questionsList);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      alert('Sorular yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/game-job-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setJobStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching job status:', error);
    }
  };

  const handleTriggerAI = async () => {
    try {
      setAiLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/trigger-game-questions`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(response.data.message || 'Yapay Zeka oyun sorularını başarıyla üretti!');
        fetchQuestions(); // Refresh the list
      }
    } catch (error: any) {
      console.error('AI trigger error:', error);
      alert('Yapay Zeka tetiklenirken hata oluştu');
    } finally {
      setAiLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      setSnackbar({ open: true, message: 'Lütfen soru verisi girin', severity: 'error' });
      return;
    }

    setBulkImportLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const questionsToImport = JSON.parse(bulkImportText);

      if (!Array.isArray(questionsToImport)) {
        throw new Error('Veri bir dizi olmalı');
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/game/questions/bulk`,
        { questions: questionsToImport },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const { successCount, skippedCount, errorCount } = response.data.data;
        setSnackbar({
          open: true,
          message: `${successCount} soru eklendi, ${skippedCount} soru zaten mevcut (atlandı), ${errorCount} hata`,
          severity: successCount > 0 ? 'success' : 'error'
        });

        if (response.data.data.successCount > 0) {
          setBulkImportDialogOpen(false);
          setBulkImportText('');
          fetchQuestions();
        }
      }
    } catch (error) {
      console.error('Error parsing bulk import:', error);
      setSnackbar({ open: true, message: 'JSON formatı hatalı veya sunucu hatası', severity: 'error' });
    } finally {
      setBulkImportLoading(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!formData.answer || !formData.hint1 || !formData.hint2 || !formData.hint3 || !formData.hint4) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      await axios.post(
        `${API_BASE_URL}/api/game/questions`,
        {
          answer: formData.answer,
          hints: [formData.hint1, formData.hint2, formData.hint3, formData.hint4],
          difficulty: formData.difficulty,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      alert('Soru başarıyla eklendi!');
      setAddDialogOpen(false);
      resetForm();
      fetchQuestions();
    } catch (error: any) {
      console.error('Error adding question:', error);
      const errorMessage = error.response?.data?.message || 'Soru eklenirken hata oluştu';
      alert(errorMessage);
    }
  };

  const handleEditQuestion = async () => {
    if (!selectedQuestion) return;

    if (!formData.answer || !formData.hint1 || !formData.hint2 || !formData.hint3 || !formData.hint4) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');

      await axios.put(
        `${API_BASE_URL}/api/game/questions/${selectedQuestion.id}`,
        {
          answer: formData.answer,
          hints: [formData.hint1, formData.hint2, formData.hint3, formData.hint4],
          difficulty: formData.difficulty,
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      alert('Soru başarıyla güncellendi!');
      setEditDialogOpen(false);
      setSelectedQuestion(null);
      resetForm();
      fetchQuestions();
    } catch (error) {
      console.error('Error updating question:', error);
      alert('Soru güncellenirken hata oluştu');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      const token = localStorage.getItem('adminToken');

      await axios.delete(
        `${API_BASE_URL}/api/game/questions/${questionToDelete.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      alert('Soru başarıyla silindi!');
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Soru silinirken hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      answer: '',
      hint1: '',
      hint2: '',
      hint3: '',
      hint4: '',
      difficulty: 'medium',
    });
  };

  const openEditDialog = (question: CoffeeQuestion) => {
    setSelectedQuestion(question);
    setFormData({
      answer: question.answer,
      hint1: question.hints[0] || '',
      hint2: question.hints[1] || '',
      hint3: question.hints[2] || '',
      hint4: question.hints[3] || '',
      difficulty: question.difficulty,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (question: CoffeeQuestion) => {
    setQuestionToDelete(question);
    setDeleteDialogOpen(true);
  };

  const filteredQuestions = questions.filter(q =>
    q.answer.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedQuestions = filteredQuestions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'Kolay';
      case 'medium': return 'Orta';
      case 'hard': return 'Zor';
      default: return difficulty;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#8B4513' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Quiz sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Günlük Oyun Yönetimi
          </Typography>
          <IconButton color="inherit" onClick={fetchQuestions}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
          <Card sx={{ p: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="h4" color="primary">{questions.length}</Typography>
            <Typography variant="body2" color="textSecondary">Toplam Soru</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#e8f5e9' }}>
            <Typography variant="h4" color="success.main">
              {questions.filter(q => q.timesAsked === 0).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">Hiç Kullanılmamış</Typography>
          </Card>
          <Card sx={{ p: 2, bgcolor: '#f3e5f5' }}>
            <Typography variant="h4" color="secondary">
              {questions.filter(q => q.timesAsked > 0).length}
            </Typography>
            <Typography variant="body2" color="textSecondary">Kullanılmış Sorular</Typography>
          </Card>
        </Box>

        {/* AI Job Status Info */}
        {jobStatus && (
          <Alert
            severity={jobStatus.status === 'error' ? 'error' : 'info'}
            sx={{ mb: 3, bgcolor: '#fff' }}
            icon={<AutoAwesome />}
          >
            <strong>Yapay Zeka Durumu:</strong> {jobStatus.status === 'success' ? 'Tamamlandı' : jobStatus.status === 'active' ? 'Çalışıyor...' : 'Beklemede'}
            {' | '}
            <strong>Bugün Üretilen:</strong> {jobStatus.questionsAdded || 0} / {jobStatus.target || 20}
            {jobStatus.lastRunTime && (
              <>
                {' | '}
                <strong>Son Çalışma:</strong> {new Date(jobStatus.lastRunTime._seconds * 1000).toLocaleString('tr-TR')}
              </>
            )}
          </Alert>
        )}

        {/* Daily Question and Leaderboard */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          {/* Daily Question Card */}
          <Card>
            <Box sx={{ p: 2, bgcolor: '#8B4513', color: 'white' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Quiz />
                Günün Sorusu
              </Typography>
            </Box>
            <CardContent>
              {dailyQuestion ? (
                <Box>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#8B4513' }}>
                    {dailyQuestion.answer}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip
                      label={`${dailyQuestion.wordCount} Harf`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={getDifficultyLabel(dailyQuestion.difficulty)}
                      color={getDifficultyColor(dailyQuestion.difficulty) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Başlangıç: {new Date(dailyQuestion.availableAt).toLocaleString('tr-TR')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Bitiş: {new Date(dailyQuestion.expiresAt).toLocaleString('tr-TR')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="textSecondary">
                    Bugün için soru atanmamış
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Leaderboard Card */}
          <Card>
            <Box sx={{ p: 2, bgcolor: '#8B4513', color: 'white' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Star />
                Liderlik Sıralaması (Top 5)
              </Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              {leaderboard.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sıra</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Kullanıcı</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Puan</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>Oyun</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.map((entry, index) => (
                      <TableRow key={entry.userId}>
                        <TableCell>
                          <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                              bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : 'default',
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={entry.photoURL} sx={{ width: 24, height: 24 }} />
                            <Typography variant="body2">{entry.username}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {entry.totalScore}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            {entry.gamesPlayed}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography color="textSecondary">
                    Henüz lider yok
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Monthly Winners */}
        {monthlyWinners.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: '#FFD700', color: '#000' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <Star />
                Geçen Ayın Kazananları ({monthlyWinners[0]?.monthYear})
              </Typography>
            </Box>
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
                {monthlyWinners.map((winner) => (
                  <Card
                    key={winner.userId}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: winner.rank === 1 ? '#FFD700' : winner.rank === 2 ? '#C0C0C0' : '#CD7F32',
                      color: 'white'
                    }}
                  >
                    <Avatar
                      src={winner.photoURL}
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        border: '3px solid white'
                      }}
                    />
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
                      {winner.rank === 1 ? '🥇' : winner.rank === 2 ? '🥈' : '🥉'} {winner.badge}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {winner.username}
                    </Typography>
                    <Chip
                      label={`${winner.rewardAmount} TL Ödül`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px'
                      }}
                    />
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Search and Add */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Soru ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Quiz />}
              onClick={handleTriggerAI}
              disabled={aiLoading}
              sx={{
                background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                color: 'white',
                boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                whiteSpace: 'nowrap'
              }}
            >
              {aiLoading ? 'Üretiliyor...' : 'Yapay Zeka ile Üret'}
            </Button>

            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => setBulkImportDialogOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              İçe Aktar
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#704010' }, whiteSpace: 'nowrap' }}
            >
              Yeni Soru Ekle
            </Button>
          </Box>
        </Paper>

        {/* Questions Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>Cevap</strong></TableCell>
                <TableCell><strong>İpuçları</strong></TableCell>
                <TableCell><strong>Zorluk</strong></TableCell>
                <TableCell><strong>Kullanım</strong></TableCell>
                <TableCell><strong>Son Kullanım</strong></TableCell>
                <TableCell align="right"><strong>İşlemler</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : paginatedQuestions.length > 0 ? (
                paginatedQuestions.map((question) => (
                  <TableRow key={question.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {question.answer}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {question.wordCount} kelime
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {question.hints.map((hint, idx) => (
                          <Typography key={idx} variant="caption">
                            {idx + 1}. {hint.substring(0, 30)}...
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getDifficultyLabel(question.difficulty)}
                        color={getDifficultyColor(question.difficulty)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${question.timesAsked}x`}
                        color={question.timesAsked === 0 ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {question.lastUsedDate
                        ? new Date(question.lastUsedDate).toLocaleDateString('tr-TR')
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openEditDialog(question)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(question)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Soru bulunamadı
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredQuestions.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Box>

      {/* Add Question Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Soru Ekle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Cevap"
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Örn: Türk Kahvesi"
            />
            <TextField
              fullWidth
              label="İpucu 1"
              value={formData.hint1}
              onChange={(e) => setFormData({ ...formData, hint1: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 2"
              value={formData.hint2}
              onChange={(e) => setFormData({ ...formData, hint2: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 3"
              value={formData.hint3}
              onChange={(e) => setFormData({ ...formData, hint3: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 4"
              value={formData.hint4}
              onChange={(e) => setFormData({ ...formData, hint4: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              select
              label="Zorluk"
              value={formData.difficulty}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
            >
              <MenuItem value="easy">Kolay</MenuItem>
              <MenuItem value="medium">Orta</MenuItem>
              <MenuItem value="hard">Zor</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>İptal</Button>
          <Button onClick={handleAddQuestion} variant="contained" color="primary">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Soru Düzenle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Cevap"
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="İpucu 1"
              value={formData.hint1}
              onChange={(e) => setFormData({ ...formData, hint1: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 2"
              value={formData.hint2}
              onChange={(e) => setFormData({ ...formData, hint2: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 3"
              value={formData.hint3}
              onChange={(e) => setFormData({ ...formData, hint3: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="İpucu 4"
              value={formData.hint4}
              onChange={(e) => setFormData({ ...formData, hint4: e.target.value })}
              sx={{ mb: 2 }}
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              select
              label="Zorluk"
              value={formData.difficulty}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
            >
              <MenuItem value="easy">Kolay</MenuItem>
              <MenuItem value="medium">Orta</MenuItem>
              <MenuItem value="hard">Zor</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
          <Button onClick={handleEditQuestion} variant="contained" color="primary">
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Soruyu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{questionToDelete?.answer}" sorusunu silmek istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteQuestion} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportDialogOpen} onClose={() => setBulkImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Toplu Soru İçe Aktarma</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            JSON formatında sorular yapıştırın. Her soru şu alanları içermelidir:
            <br />
            - answer: Cevap (Kelime)
            <br />
            - hints: En az 1 ipucu içeren dizi
            <br />
            - difficulty: "easy", "medium" veya "hard" (opsiyonel)
          </Alert>

          <TextField
            fullWidth
            multiline
            rows={15}
            placeholder={`[
  {
    "answer": "ESPRESSO",
    "hints": ["İtalya kökenli", "Basınçla demlenir", "Küçük fincanda sunulur"],
    "difficulty": "medium"
  },
  {
    "answer": "LATTE",
    "hints": ["Sütlü kahve", "İtalyanca süt demek", "Espresso bazlı"],
    "difficulty": "easy"
  }
]`}
            value={bulkImportText}
            onChange={(e) => setBulkImportText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkImportDialogOpen(false)}>İptal</Button>
          <Button
            onClick={handleBulkImport}
            variant="contained"
            color="primary"
            disabled={bulkImportLoading}
          >
            {bulkImportLoading ? 'İçe Aktarılıyor...' : 'İçe Aktar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GameManagementPage;
